import {
  ArrowRightIcon,
  Box,
  Button,
  ExternalLinkIcon,
  InfoIcon,
  Link,
  RefreshIcon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TablePagination,
  TableRow,
  TableToolbar,
  TableToolbarActions,
  Text,
  Tooltip,
  TooltipInteractiveWrapper,
} from '@razorpay/blade/components';
import api from 'api';
import { getErrorsAsString } from 'components/Kyc/utils';
import { routePaths } from 'components/Routes/data';
import { StatusBadge } from 'components/SalaryComponents/components/StatusBadge';
import { TextWithDescription } from 'components/SalaryComponents/components/TextWithDescription';
import { StatusFilterKeyType } from 'components/SalaryComponents/components/filters/StatusFilter';
import {
  deductionFiltersMap,
  helpArticles,
  salaryComponentParams,
  salaryComponentsQueries,
} from 'components/SalaryComponents/constants';
import { useDetailsView } from 'components/SalaryComponents/hooks';
import { DeductionsComponentType } from 'components/SalaryComponents/types';
import { getTableItemCountLimits, getTaxRegimeText } from 'components/SalaryComponents/utils';
import { deductionType } from 'components/WizardViews/SalaryComponents/constants';
import {
  checkIsDeductionTypeRecurring,
  deductionsExemptionDisplayText,
} from 'components/WizardViews/SalaryComponents/utils';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { ListViewLoader } from 'components/ui/ListViewLoader';
import { useFilters } from 'hooks/useFilters';
import { usePagination } from 'hooks/usePagination';
import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { AppError } from 'utils/AppError';
import { getQueryVariables } from '../../utils';

const DeductionsListView = ({
  onCountChange,
}: {
  onCountChange: (counts: Record<StatusFilterKeyType, number>) => void;
}) => {
  const history = useHistory();

  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const { currentFilters, isAnyFilterApplied } = useFilters({
    filtersMap: deductionFiltersMap,
    multipleValueFilters: [deductionFiltersMap.taxability],
    onParamsChange(params) {
      setCurrentPage(0);
    },
  });

  const queryVariables = getQueryVariables('deductions', currentFilters);

  const { openDetailsView } = useDetailsView(salaryComponentParams.componentId);

  const { data, isFetching, refetch, error, isError } = useQuery({
    queryKey: [salaryComponentsQueries.getSalaryComponentsList, queryVariables],
    queryFn: () =>
      api.salaryComponents.queries.listSalaryComponents<DeductionsComponentType>(queryVariables),
    select(data) {
      return data.payrollListSalaryComponents.data.salaryComponents;
    },
    onError: (error: AppError) => {},
  });

  const nodes = data ?? [];

  const isTableEmpty = nodes.length === 0;

  const errorString = getErrorsAsString(error as AppError);

  // Use useEffect to calculate and set counts only when needed
  useEffect(() => {
    if (onCountChange && nodes.length > 0) {
      onCountChange({
        all: nodes.length,
        active: nodes.filter((node) => node.isActive).length,
        disabled: nodes.filter((node) => !node.isActive).length,
      });
    }
  }, [nodes, onCountChange]);

  return (
    <>
      <Table
        data={{ nodes }}
        pagination={
          !isTableEmpty ? (
            <TablePagination
              currentPage={currentPage}
              showPageSizePicker={true}
              onPageChange={({ page }) => {
                setCurrentPage(page);
              }}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
              }}
              totalItemCount={nodes.length}
            />
          ) : undefined
        }
        toolbar={
          <TableToolbar title={getTableItemCountLimits(nodes.length, currentPage, itemsPerPage)}>
            <TableToolbarActions>
              <Button
                icon={RefreshIcon}
                variant={'tertiary'}
                onClick={() => {
                  refetch();
                }}
              />
            </TableToolbarActions>
          </TableToolbar>
        }>
        {(tableData) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>
                  <Text weight="semibold">Component Name</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Box display={'flex'} flexDirection={'row'} gap={'spacing.3'}>
                    <Text weight="semibold">Display Name</Text>
                    <Tooltip
                      placement="top"
                      content={`The name that will appear in employee's payslip and Salary Register`}>
                      <TooltipInteractiveWrapper>
                        <InfoIcon marginTop={'spacing.1'} />
                      </TooltipInteractiveWrapper>
                    </Tooltip>
                  </Box>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Text weight="semibold">Deduction Type</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Text weight="semibold">Taxability</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Text weight="semibold" marginX={'auto'}>
                    Status
                  </Text>
                </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            {isFetching ? (
              <ListViewLoader columnCount={5} accessibilityLabel="loading deductions components" />
            ) : (
              <TableBody>
                {tableData.map((salaryComponent) => {
                  const exemptionText = deductionsExemptionDisplayText(
                    salaryComponent.settings.behaviour.taxBehaviour?.taxExemptionSection,
                  );
                  return (
                    <TableRow
                      key={salaryComponent.id}
                      item={salaryComponent}
                      onClick={() => openDetailsView(salaryComponent.id)}
                      hoverActions={
                        <Button
                          variant="tertiary"
                          color="primary"
                          size="medium"
                          icon={ArrowRightIcon}
                          iconPosition="right"
                          onClick={() => openDetailsView(salaryComponent.id)}>
                          View Details
                        </Button>
                      }>
                      <TableCell>
                        <TextWithDescription
                          componentName={salaryComponent.name}
                          description={salaryComponent.isPredefined ? 'Predefined' : 'Custom'}
                        />
                      </TableCell>
                      <TableCell>
                        {salaryComponent.settings.behaviour.displayBehaviour?.displayName ?? ''}
                      </TableCell>
                      <TableCell>
                        {salaryComponent.settings?.deductionDetails
                          ? checkIsDeductionTypeRecurring(
                              salaryComponent.settings.deductionDetails.deductionType,
                            )
                            ? deductionType.RECURRING.label
                            : deductionType.ADHOC.label
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <TextWithDescription
                          componentName={`${
                            Boolean(
                              salaryComponent.settings.behaviour.taxBehaviour
                                ?.taxExemptInOldRegime ||
                                salaryComponent.settings.behaviour.taxBehaviour
                                  ?.taxExemptInNewRegime,
                            )
                              ? 'Exempted'
                              : 'Taxable'
                          }${
                            salaryComponent.settings.behaviour.taxBehaviour?.taxExemptionSection
                              ? ` | ${exemptionText}`
                              : ''
                          }`}
                          description={getTaxRegimeText(
                            salaryComponent.settings.behaviour.taxBehaviour?.taxExemptInOldRegime,
                            salaryComponent.settings.behaviour.taxBehaviour?.taxExemptInNewRegime,
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Box marginX={'auto'}>
                          <StatusBadge isActive={salaryComponent.isActive} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </>
        )}
      </Table>
      {isTableEmpty && !isFetching && !isError && (
        <EmptyListView
          title={
            isAnyFilterApplied
              ? 'No Search Results Found'
              : 'No Deduction components created yet 🙈'
          }
          description={
            <Text color={'surface.text.gray.muted'} weight={'medium'} textAlign={'center'}>
              Click on <b>Create Component</b> button to create a component
            </Text>
          }>
          <Box display={'flex'} flexDirection={'column'} gap={'spacing.4'} marginX={'auto'}>
            <Link
              size="medium"
              variant="anchor"
              href={helpArticles.learnMore} // link to be added
              icon={ExternalLinkIcon}
              iconPosition="right"
              target="_blank">
              Learn more about salary components
            </Link>
            <Box marginX={'auto'}>
              <Button
                size="medium"
                onClick={() => {
                  history.push(routePaths.salaryComponents.create.deductions.root);
                }}
                isFullWidth>
                Create Deductions Component
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
      {isTableEmpty && isError && (
        <EmptyListView
          title={`There was an error while fetching the data for ${
            currentFilters.status ?? 'all'
          } deduction components. `}
          description={errorString ? `Error: ${getErrorsAsString(error)}` : null}>
          <Box display={'flex'} flexDirection={'column'} gap={'spacing.4'} marginX={'auto'}>
            <Link
              size="medium"
              variant="anchor"
              href={helpArticles.learnMore} // link to be added
              icon={ExternalLinkIcon}
              iconPosition="right"
              target="_blank">
              Learn more about salary component
            </Link>
            <Box marginX={'auto'}>
              <Button
                size="medium"
                onClick={() => {
                  refetch();
                }}
                isFullWidth
                iconPosition="left"
                icon={RefreshIcon}>
                Retry
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
    </>
  );
};

export default DeductionsListView;
